import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faXmark } from '@fortawesome/free-solid-svg-icons';
import NavigationUser from '../../Layouts/Navigation/NavigationUser';
import { BASE_URL } from '../../utils/constants';
import useAutoDismissSnack from '../../Hooks/AutoDismissSnack';

function UserVehicles() {
  const [vehicles, setVehicles] = useState([]);

  // Snack notifications
  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState("");
  const [snackMessage, setSnackMessage] = useState("");

  useAutoDismissSnack(openSnack, setOpenSnack);

  const userId = sessionStorage.getItem('id_usuario');

  useEffect(() => {
    getVehicles();
  }, []);

  const getVehicles = () => {
    axios.get(`${BASE_URL}/vehicles/user/${userId}`)
      .then(response => setVehicles(response.data))
      .catch(error => console.error('Error fetching vehicles:', error));
  };

  return (
    <>
      <NavigationUser />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Snackbar Alert */}
          {openSnack && (
            <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${
              snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white animate-fade-in-down`} style={{ zIndex: 9999 }}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{snackMessage}</span>
                <button onClick={() => setOpenSnack(false)} className="ml-2 hover:bg-white/20 rounded p-1 transition-colors">
                  <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <FontAwesomeIcon icon={faCar} className="h-10 w-10 text-lime-600" />
              <h1 className="text-4xl font-bold text-gray-800">Vehículos del Apartamento</h1>
            </div>
            
            {/* Mensaje informativo */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg shadow-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 font-semibold">
                    ℹ️ Vehículos registrados
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Aquí se muestran todos los vehículos registrados para tu apartamento. Para agregar, modificar o eliminar vehículos, por favor comuníquese directamente con el área de administración del conjunto.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <div key={vehicle.id_vehiculo} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all p-6">
                <div className="mb-4">
                  <div className="relative w-full" style={{ paddingTop: '100%' }}>
                    {vehicle.foto ? (
                      <img src={`${BASE_URL}${vehicle.foto}`} alt={vehicle.placa} className="absolute inset-0 w-full h-full object-cover rounded-xl" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
                        <FontAwesomeIcon icon={faCar} className="h-10 w-10 text-lime-500" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-lime-500 to-green-600 rounded-xl">
                      <FontAwesomeIcon icon={faCar} className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{vehicle.placa}</h3>
                      <p className="text-sm text-gray-500">Parqueadero #{vehicle.num_parqueadero || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Propietario:</span>
                    <span className="text-sm font-semibold text-gray-800">
                      {vehicle.usuario_nombre} {vehicle.usuario_apellido}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Marca:</span>
                    <span className="text-sm font-semibold text-gray-800">{vehicle.marca}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Modelo:</span>
                    <span className="text-sm font-semibold text-gray-800">{vehicle.modelo}</span>
                  </div>
                  {vehicle.telefono && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Teléfono:</span>
                      <span className="text-sm font-semibold text-blue-600">{vehicle.telefono}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Torre:</span>
                    <span className="text-sm font-semibold text-gray-800">{vehicle.torre_nombre || 'N/A'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {vehicles.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faCar} className="h-24 w-24 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No hay vehículos registrados en este apartamento</p>
              <p className="text-sm text-gray-400 mt-2">Comunícate con administración para registrar vehículos</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default UserVehicles;
